import { useCallback } from 'react';
import { TFunction } from 'i18next';
import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack';

import { SECURE_API } from 'src/api/api';
import { isObjectId } from 'src/helpers/isObjectId';

type EnqueueSnackbar = (
  message: SnackbarMessage,
  options?: OptionsObject,
) => SnackbarKey;

interface CheckinPreviewResponse {
  registration: { id: string; participantId: string; editionId: string };
  participant: { id: string; firstName: string; lastName: string; ownerUserId?: string | null };
  checkin: { id: string } | null;
}

export interface QrCheckinScanResult {
  participantId: string;
  ownerUserId?: string | null;
  alreadyCheckedIn: boolean;
  participantName: string;
}

interface Params {
  editionId: string | null | undefined;
  onResolved: (result: QrCheckinScanResult) => void;
  enqueueSnackbar: EnqueueSnackbar;
  t: TFunction;
  onSuccess?: () => void;
}

function participantDisplayName(p: { firstName: string; lastName: string }): string {
  return `${p.firstName} ${p.lastName}`.trim();
}

function toScanResult(
  participant: { id: string; firstName: string; lastName: string; ownerUserId?: string | null },
  alreadyCheckedIn: boolean,
): QrCheckinScanResult {
  return {
    participantId: participant.id,
    ownerUserId: participant.ownerUserId,
    alreadyCheckedIn,
    participantName: participantDisplayName(participant),
  };
}

export function useQrCheckinScan({
  editionId,
  onResolved,
  enqueueSnackbar,
  t,
  onSuccess,
}: Params) {
  const handleScan = useCallback(
    async (raw: string) => {
      const registrationId = raw.trim();
      if (!editionId) {
        enqueueSnackbar(t('registrations.noActiveEdition'), { variant: 'error' });
        return;
      }
      if (!isObjectId(registrationId)) {
        enqueueSnackbar(t('checkins.qrInvalid'), { variant: 'error' });
        return;
      }

      try {
        const { data } = await SECURE_API.get<CheckinPreviewResponse>(
          `/registrations/${registrationId}/checkin-preview`,
          { params: { editionId } },
        );
        onSuccess?.();
        onResolved(toScanResult(data.participant, !!data.checkin));
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { errorCode?: string } } };
        const errorCode = axiosErr.response?.data?.errorCode;
        if (errorCode === 'qrWrongEdition') {
          enqueueSnackbar(t('checkins.qrWrongEdition'), { variant: 'error' });
        } else if (errorCode === 'qrNotFound') {
          enqueueSnackbar(t('checkins.qrNotFound'), { variant: 'error' });
        } else {
          enqueueSnackbar(t('checkins.qrNotFound'), { variant: 'error' });
        }
      }
    },
    [editionId, enqueueSnackbar, onSuccess, onResolved, t],
  );

  return { handleScan };
}
